import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Organization, ParsedResumeContent } from "../../Interface/Index";
import { Userprofile } from "../../Entities/user-profile.entity";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import { Button, Flex, Spin, Typography } from "antd";
import {
  ArrowRightOutlined,
  EyeFilled,
  Loading3QuartersOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ResumeUploader from "../ResumeUploader";
import ResumeViewerDrawer from "../ResumeViewerDrawer/ResumeViewerDrawer";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";
import { openNotification } from "../Notification/Index";
import { useAuth } from "../../Context/AuthContext/AuthContext";

interface UploadResumeCTAProps {
  organization: Organization;
}

const UploadResumeCTA: React.FC<UploadResumeCTAProps> = ({ organization }) => {
  const navigate = useNavigate();
  const { setLoading, setLoadingMessage } = useLoader();
  const [open, setOpen] = useState<boolean>(false);
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<Userprofile | null>(null);
  const [isFetchingProfile, setIsFetchingProfile] = useState<boolean>(false);

  const { isUserLoggedIn } = useAuth();

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleViewResume = () => {
    if (userProfile) {
      setViewerOpen(true);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  const handleViewProfile = () => {
    navigate("/profile");
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsFetchingProfile(true);
      const userProfile =
        await new ProfileMatcherService().getAllUploadedProfiles(true);
      const directUploadedProfile = userProfile?.find(
        (profile: Userprofile) => profile.isDirectUpload,
      );
      setUserProfile(directUploadedProfile || null);
      setIsFetchingProfile(false);
    };
    if (isUserLoggedIn) {
      fetchUserProfile();
    }
  }, [isUserLoggedIn]);

  // Handler for saving resume (direct upload)
  const handleSaveResume = async (
    file: File,
    resumeData: ParsedResumeContent,
    plainText: string,
  ) => {
    setLoading(true);
    setLoadingMessage("Uploading and saving your resume...");
    try {
      // Send both file and structured resume data
      const response = await new ProfileMatcherService().saveUserResume(
        file,
        resumeData,
      );
      if (response?.userProfile) {
        setUserProfile(response.userProfile);
        openNotification(
          "success",
          "Upload Successful",
          "Your resume has been uploaded and saved successfully.",
        );
      }
    } catch (err: any) {
      console.error("Error saving resume:", err);
      openNotification(
        "error",
        "Upload Error",
        err?.response?.data?.message ||
          "Failed to upload resume. Please try again.",
      );
      throw err;
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const renderUploadResumeCTAContent = () => {
    return (
      <div className="upload_resume_cta_content">
        <Typography className="upload_resume_cta_message">
          Share your resume and we’ll reach out when something opens up!
        </Typography>
        <Button
          className=""
          type="primary"
          shape="round"
          size={"middle"}
          onClick={handleOpenModal}
        >
          Continue <ArrowRightOutlined />
        </Button>
      </div>
    );
  };

  const renderAlreadyUploadedResumeContent = (profile: Userprofile) => {
    return (
      <div className="resume-info">
        <Spin
          spinning={isFetchingProfile}
          indicator={<Loading3QuartersOutlined spin />}
        >
          <div className="upload_resume_cta_content">
            <Typography className="upload_resume_cta_message">
              You have already uploaded your resume.
            </Typography>
            <Flex align="center" justify="center" gap={8} wrap="wrap">
              <Button
                className=""
                type="link"
                shape="round"
                size={"middle"}
                onClick={handleViewResume}
                icon={<EyeFilled />}
              >
                View Resume
              </Button>
              <Button
                className=""
                type="link"
                shape="round"
                size={"middle"}
                onClick={handleViewProfile}
                icon={<UserOutlined />}
              >
                View Profile
              </Button>
              <Button
                className=""
                type="primary"
                shape="round"
                size={"middle"}
                onClick={handleOpenModal}
              >
                Update Resume
              </Button>
            </Flex>
          </div>
        </Spin>
      </div>
    );
  };

  return (
    <>
      <div
        className="upload_resume_cta"
        style={{ backgroundColor: organization?.theme?.secondaryColor }}
      >
        {userProfile
          ? renderAlreadyUploadedResumeContent(userProfile)
          : renderUploadResumeCTAContent()}
        <ResumeUploader
          isOpen={open}
          handleClose={handleCloseModal}
          isDirectUpload={true}
          onSave={handleSaveResume}
          onSaveSuccess={(profile) => {
            setUserProfile(profile);
            handleCloseModal();
          }}
        />
      </div>
      <ResumeViewerDrawer
        open={viewerOpen}
        onClose={handleCloseViewer}
        profileId={userProfile?._id}
      />
    </>
  );
};

export default UploadResumeCTA;
